import { normalizeTagUid } from "@eagledispatch/shared";

export type ScanResult = { uid: string } | { error: string };

type NFCResolvePayload = {
  requestId: string;
  uid?: string;
  error?: string;
};

interface WebKitNFCBridge {
  postMessage: (message: { requestId: string }) => void;
}

interface WebKitMessageHandlers {
  eagleNFC?: WebKitNFCBridge;
}

interface WebKitWindow {
  messageHandlers?: WebKitMessageHandlers;
}

interface NDEFReaderInstance {
  scan: () => Promise<void>;
  stop?: () => void;
  addEventListener: (
    type: string,
    listener: (event: { serialNumber?: string }) => void,
  ) => void;
  removeEventListener: (
    type: string,
    listener: (event: { serialNumber?: string }) => void,
  ) => void;
}

interface NDEFReaderConstructor {
  new (): NDEFReaderInstance;
}

declare global {
  interface Window {
    webkit?: WebKitWindow;
    __eagleNFCDiag?: (msg: unknown) => void;
    __eagleNFCDiagLog?: string[];
    NDEFReader?: NDEFReaderConstructor;
  }
}

/**
 * Scans an NFC tag and returns its normalized hexadecimal UID.
 * Supports iOS WKWebView bridge (window.webkit.messageHandlers.eagleNFC)
 * and Web NFC (NDEFReader on Android Chrome).
 */
export async function scanTagUid(timeoutMs = 30_000): Promise<ScanResult> {
  // 1. iOS Native Bridge Path
  if (
    typeof window !== "undefined" &&
    window.webkit?.messageHandlers?.eagleNFC
  ) {
    return new Promise<ScanResult>((resolve) => {
      (window as any).__eagleNFCDiag = (msg: unknown) => {
        const w = window as any;
        // Reset per scan (audit BUG #1): ||= alone would accumulate entries
        // across attempts, showing stale lines from previous scans.
        w.__eagleNFCDiagLog = [] as string[];
        w.__eagleNFCDiagLog.push(String(msg));
      };

      (window as any).__eagleNFCDiagLog ||= [] as string[];

      let settled = false;
      const requestId = crypto.randomUUID();

      (window as any).__eagleNFCDiagLog?.push(`page:scan-init:${requestId.slice(0, 8)}`);

      const POLL_MS = 250;

      const readCookie = (name: string): string | null => {
        const prefix = name + "=";
        for (const part of document.cookie.split(";")) {
          const c = part.trim();
          if (c.startsWith(prefix)) return c.slice(prefix.length);
        }
        return null;
      };

      const deleteCookie = (name: string) => {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      };

      const finish = (result: ScanResult) => {
        if (settled) return;
        settled = true;
        window.clearInterval(poll);
        window.clearTimeout(timer);
        resolve(result);
      };

      const timer = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          window.clearInterval(poll);
          resolve({ error: "timeout" });
        }
      }, timeoutMs);

      const poll = window.setInterval(() => {
        const raw = readCookie("eagleNFCResult");
        if (!raw) return;
        let payload: NFCResolvePayload & { events?: string[] };
        try {
          payload = JSON.parse(decodeURIComponent(raw));
        } catch {
          return; // malformed — leave for expiry; do not consume
        }
        if (payload.requestId !== requestId) return; // not ours
        deleteCookie("eagleNFCResult");
        const evts = payload.events ?? [];
        for (const e of evts) (window as any).__eagleNFCDiagLog?.push(e);
        if (payload.uid) {
          try {
            finish({ uid: normalizeTagUid(payload.uid) });
          } catch {
            finish({ error: "invalid UID from scanner" });
          }
        } else {
          finish({ error: payload.error ?? "scan failed" });
        }
      }, POLL_MS);

      try {
        window.webkit?.messageHandlers?.eagleNFC?.postMessage({ requestId });
        (window as any).__eagleNFCDiagLog?.push("page:posted");
      } catch (err) {
        finish({
          error: err instanceof Error ? err.message : "bridge error",
        });
      }
    });
  }

  // 2. Android / Browser Web NFC (NDEFReader)
  if (
    typeof window !== "undefined" &&
    "NDEFReader" in window &&
    window.NDEFReader
  ) {
    return new Promise<ScanResult>((resolve) => {
      let settled = false;
      const reader = new window.NDEFReader!();

      const onReading = (event: { serialNumber?: string }) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        detach();
        if (!event.serialNumber) {
          resolve({ error: "no serial number on tag" });
          return;
        }
        try {
          const normalized = normalizeTagUid(event.serialNumber);
          resolve({ uid: normalized });
        } catch {
          resolve({ error: "invalid UID from scanner" });
        }
      };

      const onReadingError = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        detach();
        resolve({ error: "nfc read error" });
      };

      const detach = () => {
        reader.removeEventListener("reading", onReading);
        reader.removeEventListener("readingerror", onReadingError);
        if (typeof reader.stop === "function") {
          try {
            reader.stop();
          } catch {
            // no-op if unsupported or fails
          }
        }
      };

      const timer = window.setTimeout(() => {
        if (!settled) {
          settled = true;
          detach();
          resolve({ error: "timeout" });
        }
      }, timeoutMs);

      reader.addEventListener("reading", onReading);
      reader.addEventListener("readingerror", onReadingError);

      reader.scan().catch((err: unknown) => {
        if (!settled) {
          settled = true;
          window.clearTimeout(timer);
          detach();
          resolve({
            error:
              err instanceof Error && err.name === "NotAllowedError"
                ? "nfc permission denied"
                : "nfc scan failed",
          });
        }
      });
    });
  }

  // 3. Unsupported fallback
  return { error: "unsupported" };
}
